<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from "reka-ui";
import Mousetrap from "mousetrap";
import { useRouter } from "vue-router";
import { computed, onMounted, ref } from "vue";

import { getMarkEvidences } from "@/services/UtilitiesService";
import { deleteAnnotation } from "@/services/AnnotationService";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";
import { useAnnotationStore } from "@/stores/annotation-store";

const emit = defineEmits<(e: "annotationDeleted") => void>();

const router = useRouter();
const routeParams = useCaseRouteParams();

const deleteAnnotationConfirmOpen = ref(false);

const selectedAnnotation = computed(() => {
  if (routeParams.value.annotation == null) return null;

  return useAnnotationStore().annotations.find(
    (a) => a.fileReference === routeParams.value.casefile && a.markId === routeParams.value.annotation,
  );
});

onMounted(() => {
  Mousetrap.bind(["backspace", "del"], () => {
    deleteAnnotationConfirmOpen.value = true;
  });
});

async function confirmedAnnotationDelete() {
  if (selectedAnnotation.value == null) return;

  router.push({
    name: "casefile",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: routeParams.value.casefile,
    },
  });
  await deleteAnnotation(selectedAnnotation.value);

  emit("annotationDeleted");

  deleteAnnotationConfirmOpen.value = false;
}
</script>
<template>
  <DialogRoot v-if="selectedAnnotation != null" v-model:open="deleteAnnotationConfirmOpen">
    <DialogPortal>
      <DialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-h-full tw-w-full tw-bg-black/30" />
      <DialogContent class="tw-fixed tw-inset-0 tw-z-[1000000] tw-flex tw-items-center tw-justify-center">
        <div
          class="tw-w-full tw-max-w-md tw-space-y-2 tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-4 tw-shadow-lg"
        >
          <DialogTitle class="tw-text-balance tw-text-center tw-text-lg tw-font-semibold">
            Delete Annotation "{{ selectedAnnotation.markId }}"
          </DialogTitle>
          <DialogDescription class="tw-flex tw-items-center tw-gap-2 tw-rounded-md">
            <template v-if="getMarkEvidences({ uid: selectedAnnotation.markId }).length > 0">
              This annotation has associated bookmarks. Are you sure you'd like to delete this annotation and all its
              associated bookmarks?
            </template>
            <template v-else>Are you sure you'd like to delete this annotation?</template>
          </DialogDescription>
          <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <DialogClose class="tw-btn-md-green tw-min-w-20">Close</DialogClose>
            <button type="button" class="tw-btn-md-gray tw-min-w-20" @click="confirmedAnnotationDelete">
              Yes, I'm sure
            </button>
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
